import { useState, useEffect } from "react";
import '../stylesheets/about.css';

const fallbackSkills = [
  { name: 'JavaScript', color: '#f7dc6f', category: 'frontend' },
  { name: 'TypeScript', color: '#4fc3f7', category: 'frontend' },
  { name: 'React', color: '#61dafb', category: 'frontend' },
  { name: 'HTML/CSS', color: '#e8845c', category: 'frontend' },
  { name: 'SASS', color: '#c69', category: 'frontend' },
  { name: 'jQuery', color: '#78beff', category: 'frontend' },
  { name: 'PHP', color: '#aab2d5', category: 'backend' },
  { name: 'Laravel', color: '#fb503b', category: 'backend' },
  { name: 'Node.js', color: '#6cc24a', category: 'backend' },
  { name: 'Express.js', color: '#999', category: 'backend' },
  { name: 'MySQL / SQL', color: '#00758f', category: 'database' },
  { name: 'MongoDB', color: '#4db33d', category: 'database' },
  { name: 'Git', color: '#f05032', category: 'tools' },
  { name: 'Docker', color: '#2496ed', category: 'tools' },
  { name: 'REST APIs', color: '#00d4aa', category: 'tools' },
  { name: 'Cloud Servers', color: '#9b59b6', category: 'tools' },
];

const fallbackExperiences = [
  {
    company: 'Traveware',
    role: 'Backend Developer',
    period: 'Jul 2025 — Present',
    points: [
      'Developed RESTful APIs for hotel and flight booking operations',
      'Integrated travel supplier APIs with mapping and rate selection logic',
      'Optimized system performance using query optimization and caching',
      'Designed scalable database and backend architectures',
    ],
  },
  {
    company: 'Keden International',
    role: 'Full Stack Web Developer',
    period: 'Jan 2024 — Jul 2025',
    points: [
      'Led design and implementation of dynamic UIs and robust backend solutions',
      'Maintained and optimized company platforms',
      'Managed and maintained cloud servers',
    ],
  },
  {
    company: 'Crew Recruitment',
    role: 'Full Stack Web Developer',
    period: 'Sep 2023 — Dec 2023',
    points: [
      'Developed front-end from UI designs and back-end using Laravel & MySQL',
      'Deployed and maintained production servers',
      'Integrated WhatsApp API solutions',
    ],
  },
  {
    company: 'Infinity Distribution',
    role: 'IT Engineer / Web Developer',
    period: 'Aug 2022 — May 2023',
    points: [
      'Developed a web application for managing device maintenance records',
      'Operated a UPS service center',
    ],
  },
];

function About() {
  const [skills, setSkills] = useState(fallbackSkills);
  const [experiences, setExperiences] = useState(fallbackExperiences);

  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    // Fetch Skills
    fetch('/skills.php', { method: 'GET', signal: controller.signal })
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          const normalized = data.map(s => ({
            name: s.name || 'Skill',
            color: s.color || '#6c63ff',
            category: s.category || 'frontend'
          }));
          setSkills(normalized);
        }
      })
      .catch(() => { });

    // Fetch Experience
    fetch('/experience.php', { method: 'GET', signal: controller.signal })
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          const normalized = data.map(e => ({
            company: e.company || 'Company',
            role: e.role || 'Developer',
            period: e.period || '',
            points: typeof e.points === 'string' ? e.points.split('|').map(p => p.trim()) : (Array.isArray(e.points) ? e.points : [])
          }));
          setExperiences(normalized);
        }
      })
      .catch(() => { });

    return () => { clearTimeout(timeoutId); controller.abort(); };
  }, []);


  const [activeTab, setActiveTab] = useState(0);

  return (
    <section className="about-section reveal" id="about">

      {/* About Me */}
      <div className="about-intro reveal">
        <h2 className="section-title">
          <span className="section-number">01.</span> About Me
        </h2>
        <div className="about-content">
          <div className="about-text">
            <p>
              I'm a <strong>Software Engineer</strong> based in Egypt,
              passionate about building innovative, user-centric web applications and managing
              cloud infrastructure.
            </p>
            <p>
              With hands-on experience across the entire stack — from crafting pixel-perfect
              front-ends with React to architecting robust Laravel & Node.js backends — I thrive
              on solving complex problems with clean, maintainable code.
            </p>
            <p>
              I hold a Bachelor's in <strong>Electronics & Communications Engineering</strong> and
              have complemented it with a Full Stack bootcamp at Sprints.ai, certifications in
              Data Structures, SOLID Principles, and Design Patterns.
            </p>
          </div>
        </div>
      </div>

      {/* Skills */}
      <div className="skills-section reveal">
        <h2 className="section-title">
          <span className="section-number">02.</span> Skills &amp; Technologies
        </h2>
        <div className="skills-grid">
          {skills.map((skill, i) => (
            <span
              key={i}
              className={`skill-badge skill-${skill.category}`}
              style={{ '--badge-color': skill.color }}
            >
              {skill.name}
            </span>
          ))}
        </div>
      </div>

      {/* Experience */}
      <div className="experience-section reveal">
        <h2 className="section-title">
          <span className="section-number">03.</span> Where I've Worked
        </h2>

        <div className="experience-container">
          <div className="experience-tabs">
            {experiences.map((exp, i) => (
              <button
                key={i}
                className={`tab-button ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {exp.company}
              </button>
            ))}
            <div
              className="tab-highlight"
              style={{ transform: `translateY(${activeTab * 42}px)` }}
            ></div>
          </div>

          <div className="experience-panels">
            {experiences.map((exp, i) => (
              <div
                key={i}
                className={`tab-panel ${activeTab === i ? 'active' : ''}`}
                hidden={activeTab !== i}
              >
                <div className="panel-header">
                  <h3 className="panel-role">
                    {exp.role} <span className="panel-company">@ {exp.company}</span>
                  </h3>
                  <p className="panel-period">{exp.period}</p>
                </div>
                <ul className="panel-points">
                  {exp.points.map((point, j) => (
                    <li key={j}>{point}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

    </section>
  );
}

export default About;
